import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Search, Eye, Plus, LayoutTemplate, Loader2 } from 'lucide-react';
import { API_BASE } from '../config/api';
import { parseLayoutSchemaJson } from '../utils/templateSchemaExport';

function resolveThumbnailUrl(thumbnail) {
  if (thumbnail == null || typeof thumbnail !== 'string') return null;
  const t = thumbnail.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith('/')) return `${API_BASE}${t}`;
  return `${API_BASE}/${t.replace(/^\//, '')}`;
}

function formatTemplateDate(iso, locale) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { dateStyle: 'medium' });
}

/** Miniatura do layout real (mesmo motor do preview no App) — iframe com escala; carrega ao entrar na viewport. */
function TemplateMiniPreview({ schemaJson, buildSrcDoc, variant = 'card', className = '' }) {
  const rootRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [srcDoc, setSrcDoc] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const scale = variant === 'detail' ? 0.24 : 0.14;

  useEffect(() => {
    if (!buildSrcDoc) return undefined;
    const el = rootRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: '120px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [buildSrcDoc, schemaJson]);

  useEffect(() => {
    if (!shouldLoad || !buildSrcDoc) return;
    setLoadFailed(false);
    setSrcDoc(null);
    try {
      const schema = parseLayoutSchemaJson(schemaJson);
      const rows = schema?.rows || [];
      if (!rows.length) {
        setLoadFailed(true);
        return;
      }
      const doc = buildSrcDoc(rows, schema.globalConfig, schema.headerWidgets || [], { scale });
      if (doc && String(doc).trim()) setSrcDoc(doc);
      else setLoadFailed(true);
    } catch {
      setLoadFailed(true);
    }
  }, [shouldLoad, schemaJson, buildSrcDoc, scale]);

  if (!buildSrcDoc) return null;

  return (
    <div ref={rootRef} className={`relative overflow-hidden bg-slate-100 dark:bg-slate-800 ${className}`}>
      {srcDoc ? (
        <iframe
          title=""
          className="absolute inset-0 w-full h-full border-0 pointer-events-none bg-slate-100 dark:bg-slate-900"
          srcDoc={srcDoc}
        />
      ) : loadFailed && shouldLoad ? (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
          <LayoutTemplate className="text-slate-400" size={variant === 'detail' ? 40 : 24} />
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
          {shouldLoad ? (
            <Loader2 className="animate-spin text-slate-400" size={variant === 'detail' ? 28 : 20} />
          ) : (
            <LayoutTemplate className="text-slate-300" size={variant === 'detail' ? 36 : 22} />
          )}
        </div>
      )}
    </div>
  );
}

export default function TemplatesGalleryModal({
  open,
  onClose,
  templates = [],
  gallery = [],
  loading = false,
  errorKey = null,
  onApply,
  onPreview,
  buildTemplateGalleryPreviewSrcDoc,
  suppressEscapeClose = false,
}) {
  const { t, i18n } = useTranslation('builder');
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'pt-BR';

  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [category, setCategory] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const tm = setTimeout(() => setQDebounced(q.trim()), 280);
    return () => clearTimeout(tm);
  }, [q]);

  useEffect(() => {
    if (!open) {
      setQ('');
      setQDebounced('');
      setCategory('');
      setSelected(null);
    }
  }, [open]);

  const allItems = useMemo(() => {
    const off = (templates || []).map((item) => ({ ...item, _tplSource: 'official' }));
    const com = (gallery || []).map((item) => ({ ...item, _tplSource: 'community' }));
    return [...off, ...com];
  }, [templates, gallery]);

  const filtered = useMemo(() => {
    let list = allItems;
    if (category === 'official') list = list.filter((i) => i._tplSource === 'official');
    if (category === 'community') list = list.filter((i) => i._tplSource === 'community');
    const needle = qDebounced.toLowerCase();
    if (!needle) return list;
    return list.filter((i) => {
      const name = String(i.name ?? '').toLowerCase();
      const desc = String(i.description ?? '').toLowerCase();
      const designer = String(i.designer_name ?? '').toLowerCase();
      return name.includes(needle) || desc.includes(needle) || designer.includes(needle);
    });
  }, [allItems, category, qDebounced]);

  useEffect(() => {
    if (!selected) return;
    const still = filtered.some((i) => i.id === selected.id && i._tplSource === selected._tplSource);
    if (!still) setSelected(null);
  }, [filtered, selected]);

  const applyItem = useCallback(
    (item) => {
      const schema = parseLayoutSchemaJson(item.schema_json);
      const rows = schema?.rows || [];
      const headerWidgets = schema?.headerWidgets || [];
      if (rows.length > 0 && onApply) onApply(rows, schema.globalConfig, headerWidgets);
    },
    [onApply]
  );

  useEffect(() => {
    if (!open || suppressEscapeClose) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, suppressEscapeClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100020] flex items-center justify-center bg-black/55 p-3 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="templates-gallery-title"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="flex flex-col w-full max-w-6xl h-[min(92vh,900px)] rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 id="templates-gallery-title" className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <LayoutTemplate size={22} className="text-blue-600 shrink-0" />
            {t('templatesGalleryTitle', { defaultValue: 'Galeria de templates' })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
            aria-label={t('close', { defaultValue: 'Fechar' })}
          >
            <X size={22} />
          </button>
        </div>
        <p className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40">
          {t('templatesGallerySubtitle', { defaultValue: 'Modelos oficiais e da comunidade — busca e filtro por origem.' })}
        </p>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-slate-500 dark:text-slate-400 p-8">{t('loading')}</div>
        ) : errorKey ? (
          <div className="flex-1 flex items-center justify-center text-red-600 dark:text-red-400 text-sm p-8">{t(errorKey)}</div>
        ) : allItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 gap-2">
            <LayoutTemplate size={40} className="text-slate-300" />
            <p className="text-sm">{t('noTemplates')}</p>
          </div>
        ) : (
          <div className="flex flex-1 min-h-0 flex-col md:flex-row">
            <div className="flex-1 flex flex-col min-w-0 min-h-0 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700">
              <div className="p-3 flex flex-wrap gap-2 items-center border-b border-slate-100 dark:border-slate-800 shrink-0">
                <div className="relative flex-1 min-w-[160px] max-w-xl">
                  <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    type="search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t('templatesGallerySearchPlaceholder', {
                      defaultValue: 'Buscar por nome, descrição ou designer…',
                    })}
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200"
                  />
                </div>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-2 text-slate-800 dark:text-slate-200 min-w-[180px]"
                >
                  <option value="">{t('templatesGalleryCategoryAll', { defaultValue: 'Todas as origens' })}</option>
                  <option value="official">{t('templatesGalleryCategoryOfficial', { defaultValue: 'Somente oficiais' })}</option>
                  <option value="community">{t('templatesGalleryCategoryCommunity', { defaultValue: 'Somente comunidade' })}</option>
                </select>
              </div>
              <div className="flex-1 overflow-y-auto p-3 min-h-0">
                {filtered.length === 0 ? (
                  <p className="text-center text-sm text-slate-500 dark:text-slate-400 py-10">
                    {t('templatesGalleryEmptyFilter', { defaultValue: 'Nenhum resultado para esta busca ou filtro.' })}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((item) => {
                      const thumb = resolveThumbnailUrl(item.thumbnail);
                      const isSel = selected?.id === item.id && selected?._tplSource === item._tplSource;
                      const badge =
                        item._tplSource === 'official'
                          ? t('templatesSourceOfficial')
                          : t('templatesSourceCommunity');
                      return (
                        <button
                          key={`${item._tplSource}-${item.id}`}
                          type="button"
                          onClick={() => setSelected(item)}
                          className={`text-left rounded-xl border p-3 flex gap-3 transition-all bg-white dark:bg-slate-800/80 hover:border-blue-400 hover:shadow-md ${
                            isSel ? 'border-blue-500 ring-2 ring-blue-400/50 shadow-md' : 'border-slate-200 dark:border-slate-600'
                          }`}
                        >
                          <div className="w-24 h-24 shrink-0 rounded-lg overflow-hidden ring-1 ring-slate-200/80 dark:ring-slate-600">
                            {buildTemplateGalleryPreviewSrcDoc ? (
                              <TemplateMiniPreview
                                schemaJson={item.schema_json}
                                buildSrcDoc={buildTemplateGalleryPreviewSrcDoc}
                                variant="card"
                                className="h-full w-full rounded-lg"
                              />
                            ) : thumb ? (
                              <img src={thumb} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-slate-100 dark:bg-slate-700">
                                <LayoutTemplate className="text-slate-400" size={28} />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1 flex flex-col gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">{badge}</span>
                            <span className="font-semibold text-slate-900 dark:text-slate-100 text-sm leading-snug break-words line-clamp-2">
                              {item.name || '—'}
                            </span>
                            {item.description ? (
                              <span className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 break-words">{item.description}</span>
                            ) : null}
                            {item._tplSource === 'community' ? (
                              <span className="text-[11px] text-slate-600 dark:text-slate-300 mt-auto break-words">
                                {item.designer_name
                                  ? t('templatesDesigner', { name: item.designer_name })
                                  : t('templatesDesignerUnknown')}
                              </span>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <aside className="w-full md:w-[min(100%,340px)] shrink-0 flex flex-col border-t md:border-t-0 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              {selected ? (
                <div className="p-4 flex flex-col gap-3 overflow-y-auto max-h-[50vh] md:max-h-none md:flex-1 md:h-full">
                  <div className="aspect-video min-h-[140px] rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700 ring-1 ring-slate-200/80 dark:ring-slate-600 relative">
                    {buildTemplateGalleryPreviewSrcDoc ? (
                      <TemplateMiniPreview
                        schemaJson={selected.schema_json}
                        buildSrcDoc={buildTemplateGalleryPreviewSrcDoc}
                        variant="detail"
                        className="absolute inset-0 min-h-[140px]"
                      />
                    ) : resolveThumbnailUrl(selected.thumbnail) ? (
                      <img
                        src={resolveThumbnailUrl(selected.thumbnail)}
                        alt=""
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="flex h-full min-h-[140px] w-full items-center justify-center">
                        <LayoutTemplate size={48} className="text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                      {selected._tplSource === 'official' ? t('templatesSourceOfficial') : t('templatesSourceCommunity')}
                    </span>
                    <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-1 break-words">{selected.name || '—'}</h3>
                  </div>
                  {selected.description ? (
                    <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-words">{selected.description}</p>
                  ) : (
                    <p className="text-xs text-slate-400 italic">{t('templatesGalleryNoDescription', { defaultValue: 'Sem descrição.' })}</p>
                  )}
                  {selected._tplSource === 'community' ? (
                    <p className="text-sm text-slate-700 dark:text-slate-200 break-words">
                      {selected.designer_name
                        ? t('templatesDesigner', { name: selected.designer_name })
                        : t('templatesDesignerUnknown')}
                    </p>
                  ) : null}
                  {selected.created_at ? (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t('templatesGalleryDate', { defaultValue: 'Publicado' })}: {formatTemplateDate(selected.created_at, locale)}
                    </p>
                  ) : null}
                  <div className="flex flex-col gap-2 pt-2 mt-auto">
                    {onPreview ? (
                      <button
                        type="button"
                        onClick={() => {
                          onPreview(selected);
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 font-medium text-sm hover:bg-white dark:hover:bg-slate-800"
                      >
                        <Eye size={18} />
                        {t('templatesView')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        applyItem(selected);
                        onClose?.();
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm"
                    >
                      <Plus size={18} />
                      {t('templatesUse')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-sm text-slate-500 dark:text-slate-400 flex flex-col items-center justify-center min-h-[200px] md:min-h-0 md:flex-1 text-center">
                  {t('templatesGallerySelectHint', {
                    defaultValue: 'Selecione um template na lista para ver nome completo, descrição, designer e ações.',
                  })}
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
